// Camera helpers for diving into the lakes: how high the lakebed is under a
// point, and where a view ray first meets it. They sample the same terrain
// grids terrainMesh() draws, so the camera never sinks into the lakebed on
// screen. Pure functions of that data, so they run without WebGL.
import { CONTEXT_RELIEF, fromWorld } from "./geo.js";

// How far (km) the camera keeps above the lakebed or the ground, and the
// closest it can orbit its pivot.
export const CLEARANCE_KM = 0.25;
export const MIN_ORBIT_KM = 0.5;

const STEP_KM = 0.4;
const STEP_GROWTH = 0.01;
const BISECTIONS = 18;
// A ray this close above the ground counts as touching it, so rounding cannot
// let one slip past a flat floor it lands on exactly.
const TOUCH_KM = 1e-9;

// Depth (metres) of one lake's mesh at a point, interpolated over the triangles
// terrainMesh() builds: each cell is split along its north-east to south-west
// diagonal. Null outside the grid.
function meshDepth(lake, latitude, longitude) {
  const row = (lake.north - latitude) / lake.cellLatitude;
  const column = (longitude - lake.west) / lake.cellLongitude;
  if (!(row >= 0 && column >= 0 && row <= lake.rows - 1 && column <= lake.columns - 1)) return null;
  const top = Math.min(Math.floor(row), lake.rows - 2);
  const left = Math.min(Math.floor(column), lake.columns - 2);
  const southward = row - top;
  const eastward = column - left;
  const index = top * lake.columns + left;
  const northWest = lake.depths[index];
  const northEast = lake.depths[index + 1];
  const southWest = lake.depths[index + lake.columns];
  const southEast = lake.depths[index + lake.columns + 1];
  return eastward + southward <= 1
    ? northWest + (northEast - northWest) * eastward + (southWest - northWest) * southward
    : southEast + (southWest - southEast) * (1 - eastward) + (northEast - southEast) * (1 - southward);
}

// Height (km, up is positive) of what the scene draws under a point: the
// lakebed over water, 0 on land. Lakes out of focus keep part of their relief.
export function floorHeight(lakes, x, z, exaggeration, focus = null) {
  const [latitude, longitude] = fromWorld(x, z);
  let floor = -Infinity;
  for (const lake of lakes) {
    const depth = meshDepth(lake, latitude, longitude);
    if (depth === null || depth <= 0) continue;
    const relief = focus && lake.slug !== focus ? CONTEXT_RELIEF : 1;
    floor = Math.max(floor, (-depth / 1000) * exaggeration * relief);
  }
  return floor === -Infinity ? 0 : floor;
}

// The lowest the camera may sit at a point: a clearance above the highest ground
// within a clearance of it, so a slope beside the camera cannot cut through the view.
export function cameraFloor(lakes, x, z, exaggeration, focus = null, clearance = CLEARANCE_KM) {
  let ground = floorHeight(lakes, x, z, exaggeration, focus);
  for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
    ground = Math.max(ground, floorHeight(lakes, x + dx * clearance, z + dz * clearance, exaggeration, focus));
  }
  return ground + clearance;
}

// Distance along a ray (unit direction) to where it first meets the lakebed or
// the land, or null if it does not within `limit` km. Everything the ray can
// meet lies between the surface and the deepest lakebed, so only that slab is
// searched, in steps shorter than a grid cell and then refined.
export function terrainDistance(lakes, origin, direction, { exaggeration, focus = null, limit = 4000 }) {
  const touching = (distance) => (
    origin.y + direction.y * distance - floorHeight(lakes, origin.x + direction.x * distance, origin.z + direction.z * distance, exaggeration, focus) <= TOUCH_KM
  );
  if (touching(0)) return 0;
  if (origin.y > 0 && direction.y >= 0) return null;
  let deepest = 0;
  for (const lake of lakes) deepest = Math.max(deepest, lake.maxDepth);
  let start = 0;
  let end = limit;
  if (direction.y < 0) {
    start = Math.max(0, -origin.y / direction.y);
    end = Math.min(end, (-(deepest / 1000) * exaggeration - origin.y) / direction.y);
  } else if (direction.y > 0) {
    end = Math.min(end, -origin.y / direction.y);
  }
  if (!(start < end)) return null;
  if (touching(start)) return start;
  const base = STEP_KM / Math.max(Math.hypot(direction.x, direction.z), Math.abs(direction.y));
  let before = start;
  while (before < end) {
    const after = Math.min(end, before + Math.max(base, before * STEP_GROWTH));
    if (touching(after)) {
      let low = before;
      let high = after;
      for (let index = 0; index < BISECTIONS; index += 1) {
        const middle = (low + high) / 2;
        if (touching(middle)) high = middle;
        else low = middle;
      }
      return high;
    }
    before = after;
  }
  return null;
}

// Distance along a ray to a vertical wall that hangs from the surface down to the
// lakebed along the line (x0, z0) to (x1, z1) — the cross-section curtain — or
// null if the ray misses it within `limit` km.
export function wallDistance(lakes, origin, direction, wall, { exaggeration, focus = null, limit = 4000 }) {
  const lineX = wall.x1 - wall.x0;
  const lineZ = wall.z1 - wall.z0;
  const determinant = direction.x * lineZ - direction.z * lineX;
  if (Math.abs(determinant) < 1e-9) return null;
  const offsetX = wall.x0 - origin.x;
  const offsetZ = wall.z0 - origin.z;
  const distance = (offsetX * lineZ - offsetZ * lineX) / determinant;
  const along = (offsetX * direction.z - offsetZ * direction.x) / determinant;
  if (!(distance >= 0 && distance <= limit && along >= 0 && along <= 1)) return null;
  const height = origin.y + direction.y * distance;
  const floor = floorHeight(lakes, wall.x0 + lineX * along, wall.z0 + lineZ * along, exaggeration, focus);
  return height <= 0 && height >= floor ? distance : null;
}

// How far ahead of the camera the orbit pivot belongs: on the lakebed, the land or
// the cross-section the view is looking at, so orbiting and zooming work around
// what is on screen. Only ground within `reach` times the camera's height counts,
// so a level view under water gets a pivot floating a little way ahead instead of
// a far shore. Looking up from under water the pivot sits on the surface above.
// Null when an above-water view meets nothing, so the pivot stays where it is.
export function pivotDistance(lakes, origin, direction, { exaggeration, focus = null, wall = null, clearance = CLEARANCE_KM, reach = 8, minDistance = MIN_ORBIT_KM, maxDistance = 4000 }) {
  const height = origin.y - floorHeight(lakes, origin.x, origin.z, exaggeration, focus);
  const limit = Math.min(maxDistance, reach * Math.max(height, clearance));
  let distance = terrainDistance(lakes, origin, direction, { exaggeration, focus, limit });
  if (wall) {
    const across = wallDistance(lakes, origin, direction, wall, { exaggeration, focus, limit: distance ?? limit });
    if (across !== null) distance = across;
  }
  if (distance === null) {
    if (origin.y >= 0) return null;
    distance = direction.y > 0 ? Math.min(limit, -origin.y / direction.y) : limit;
  }
  return Math.max(minDistance, Math.min(maxDistance, distance));
}
